(() => {
  const CART_TIMEOUT_MS = 3500;
  const RECOVERY_WINDOW_MS = 15000;
  const RECOVERY_DELAYS_MS = [500, 700, 900, 1200, 1600, 2100, 2700, 3400];
  const delay = (milliseconds) => new Promise((resolve) => window.setTimeout(resolve, milliseconds));
  const metadataFor = (item) => window.SafeWalkV7KachingNativeContract?.metadataFor(item);
  const fetchJson = async (input, init = {}, timeoutMs = CART_TIMEOUT_MS) => {
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    let timeout;
    let timeoutArmed = false;
    const request = (async () => {
      const requestInit = { ...init };
      if (controller && !requestInit.signal) requestInit.signal = controller.signal;
      const response = await window.fetch(input, requestInit);
      return { response, body: await response.json() };
    })();
    const deadline = new Promise((_, reject) => {
      timeout = window.setTimeout(() => {
        if (!timeoutArmed) return;
        reject(new Error('cart-timeout'));
        controller?.abort();
      }, timeoutMs);
      timeoutArmed = true;
    });
    try {
      return await Promise.race([request, deadline]);
    } finally {
      window.clearTimeout?.(timeout);
    }
  };
  const fetchCart = async (timeoutMs = CART_TIMEOUT_MS) => {
    const { response, body } = await fetchJson('/cart.js', {
      headers: { Accept: 'application/json' },
      cache: 'no-store'
    }, timeoutMs);
    if (!response.ok) throw new Error(`cart-${response.status}`);
    return body;
  };
  const groupIds = (cart) => new Set(
    (cart?.items || []).map((item) => String(metadataFor(item)?.id || '')).filter(Boolean)
  );
  const snapshot = async () => {
    try {
      return { ids: [...groupIds(await fetchCart())], reliable: true };
    } catch (error) {
      return { ids: [], reliable: false };
    }
  };
  const outcomeFor = (cart, before = {}, matches) => {
    const reliable = before.reliable !== false;
    const priorIds = reliable ? new Set(before.ids || []) : new Set();
    const groups = new Map();
    (cart?.items || []).forEach((item) => {
      const id = String(metadataFor(item)?.id || '');
      if (!id || priorIds.has(id)) return;
      if (!groups.has(id)) groups.set(id, []);
      groups.get(id).push(item);
    });
    const candidates = [...groups.values()].map((items) => ({
      ok: true,
      status: 200,
      body: { items },
      recovered: true
    }));
    const matched = candidates.filter((candidate) => matches(candidate));
    if (reliable && matched.length === 1) {
      return { matched: true, ambiguous: false, empty: false, result: matched[0] };
    }
    return {
      matched: false,
      ambiguous: candidates.length > 0 || !reliable,
      empty: candidates.length === 0 && reliable,
      result: null
    };
  };
  const inspect = async ({ snapshot: before = {}, matches, timeoutMs = CART_TIMEOUT_MS }) => {
    try {
      return outcomeFor(await fetchCart(timeoutMs), before, matches);
    } catch (error) {
      return { matched: false, ambiguous: true, empty: false, result: null };
    }
  };
  const recover = ({ snapshot: before = {}, matches }) => {
    let cancelled = false;
    const promise = (async () => {
      const startedAt = Date.now();
      let ambiguous = false;
      for (const waitMs of RECOVERY_DELAYS_MS) {
        const beforeWaitMs = RECOVERY_WINDOW_MS - (Date.now() - startedAt);
        if (beforeWaitMs <= 0) break;
        await delay(Math.min(waitMs, beforeWaitMs));
        if (cancelled) return { matched: false, ambiguous: false, result: null };
        const remainingMs = RECOVERY_WINDOW_MS - (Date.now() - startedAt);
        if (remainingMs <= 0) break;
        const outcome = await inspect({
          snapshot: before,
          matches,
          timeoutMs: Math.min(CART_TIMEOUT_MS, remainingMs)
        });
        if (outcome.matched) return outcome;
        if (outcome.ambiguous) ambiguous = true;
      }
      return { matched: false, ambiguous, result: null };
    })();
    return {
      promise,
      cancel: () => {
        cancelled = true;
      }
    };
  };

  window.SafeWalkV51KachingRecovery = Object.freeze({ fetchJson, snapshot, inspect, recover });
})();
